import { useState, useCallback } from 'react';
import { ChatAPIService, Conversation, Message } from '../../services/chatService';
import { markConversationAsRead } from './helpers';

/* eslint-disable max-lines-per-function */
/**
 * Hook para operações de fetch do chat
 */
export const useChatFetch = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async (page = 1, limit = 20, status?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ChatAPIService.getConversations(page, limit, status);
      setConversations(response.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar conversas';
      setError(message);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    try {
      setLoading(true);
      setError(null);
      const conversation = await ChatAPIService.getConversation(conversationId);
      setSelectedConversation(conversation);
      if (conversation.messages) setMessages(conversation.messages);

      await ChatAPIService.markAsRead(conversationId);
      setConversations((prev) => markConversationAsRead(prev, conversationId));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar conversa';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: string, page = 1, limit = 50) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ChatAPIService.getMessages(conversationId, page, limit);
      setMessages(response.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar mensagens';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    loading,
    error,
    setError,
    loadConversations,
    loadConversation,
    loadMessages,
  };
};
